// frontend/src/pages/QuickCheck.jsx
import React, { useEffect, useMemo, useState } from "react";
import AccessoryModal from "../ui/AccessoryModal";
import SizeCheckModal from "../ui/SizeCheckModal";
import { Button, TextInput, ListBox, Modal } from "../ui/UIComponents";
import HDSD_DATA from "./hdsd.json";

/** ------------------------------
 * Shared robust fetch helper
 * (same behavior as SizeCheck.jsx)
 * ------------------------------ */
async function fetchJSON(input, init) {
  const resp = await fetch(input, init);

  try {
    const data = await resp.json();
    if (!resp.ok || data?.ok === false) {
      const msg =
        data?.details?.message || data?.error || `HTTP ${resp.status}`;
      const e = new Error(msg);
      e.status = resp.status;
      throw e;
    }
    return data;
  } catch {
    const txt = await resp.clone().text();
    const clean = (txt || "").slice(0, 300) || "Non-JSON error response";
    const e = new Error(clean);
    e.status = resp.status;
    throw e;
  }
}

/** ------------------------------
 * HELPERS
 * ------------------------------ */

// Label in PL listbox: Name — Description (Updated)
const formatPL = (row) => {
  const name = String(row.pl || row.prod_name || row.name || "");
  // Try common keys for description (desc, opt_desc, pl_desc)
  const desc = String(row.desc ?? row.opt_desc ?? row.pl_desc ?? "");

  // Return "Name — Description" if description exists
  if (name && desc) {
    return `${name} — ${desc}`;
  }
  return name || "Unnamed";
};

// Find product row by prod_id / opt_id
const findPLById = (items, id) => {
  if (!id) return null;
  const idNum = Number(id);
  return (
    items.find((x) => Number(x.prod_id ?? x.opt_id) === idNum) || null
  );
};

// Map numeric size_limit_by → UI label HW / SHW (fallback to id)
const deriveLimitLabel = (rowOrId) => {
  let raw = rowOrId;
  if (rowOrId && typeof rowOrId === "object") {
    raw = rowOrId.size_limit_by ?? rowOrId.limit_id;
  }

  const id = Number(raw);
  if (!Number.isFinite(id) || id <= 0) return "";

  if (id === 155) return "HW";
  if (id === 156) return "SHW";

  // Unknown mapping: show raw id so it’s still debuggable
  return String(id);
};

// Format an operation option label: "<opt_name>, max m2: <max m2>"
const formatOp = (row) => {
  const name = row.opt_name ?? row.name ?? "";
  const m2 = row.max_m2 != null ? String(row.max_m2) : "";
  return m2 ? `${name}, max m2: ${m2}` : name;
};

export default function QuickCheck() {
  /** ------------------------------
   * FORM STATE
   * ------------------------------ */
  const [widthMm, setWidthMm] = useState("1000");
  const [heightMm, setHeightMm] = useState("1000");
  const [selectedProdId, setSelectedProdId] = useState("");

  // numeric DB value: 155 / 156 / etc. -> sent to backend (if needed later)
  const [selectedLimitId, setSelectedLimitId] = useState(null);

  // UI-only label: "HW" / "SHW" (derived from numeric id)
  const [selectedLimitLabel, setSelectedLimitLabel] = useState("");

  /** ------------------------------
   * DATA: PRODUCT LINES
   * ------------------------------ */
  const [prodItems, setProdItems] = useState([]);
  const [loadingProds, setLoadingProds] = useState(false);

  /** ------------------------------
   * DATA: OPERATIONS
   * ------------------------------ */
  const [opsItems, setOpsItems] = useState([]);
  const [selectedOpId, setSelectedOpId] = useState("");
  const [loadingOps, setLoadingOps] = useState(false);

  /** ------------------------------
   * ADDITIONAL DATA (like SizeCheck “Thông tin bổ sung”)
   * ------------------------------ */
  const [lightLoading, setLightLoading] = useState(false);
  const [lightError, setLightError] = useState("");
  const [lightData, setLightData] = useState(null);

  /** ------------------------------
   * UI STATE
   * ------------------------------ */
  const [operationText, setOperationText] = useState(
    "Kết quả kiểm tra kích thước sẽ hiển thị tại đây.\nSize check result will appear here."
  );
  const [error, setError] = useState("");
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showAccessoryModal, setShowAccessoryModal] = useState(false);
  
  // NEW: State for "No Results" popup
  const [showNoResultsModal, setShowNoResultsModal] = useState(false);

  // ---- HDSD State
  const [guideOpen, setGuideOpen] = useState(false);
  // Using "tools" section which describes Quick Check in the JSON
  const activeGuide = HDSD_DATA["quickcheck"] || HDSD_DATA.default;

  /** ------------------------------
   * LOAD PRODUCT LINES ONCE
   * ------------------------------ */
  useEffect(() => {
    let cancelled = false;

    const loadProducts = async () => {
      setLoadingProds(true);
      setError("");
      try {
        // Uses the same helper in data.js as SizeCheck (quick_products)
        const data = await fetchJSON(
          "/api/data?scope=size&action=quick_products"
        );
        const items = Array.isArray(data.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];

        if (cancelled) return;

        setProdItems(items);

        // Default selection = first item (for faster sales workflow)
        if (items.length > 0 && !selectedProdId) {
          const first = items[0];
          const id = String(first.prod_id ?? first.opt_id);
          const limitIdRaw = first.size_limit_by ?? first.limit_id;
          const limitId = Number(limitIdRaw) || null;

          setSelectedProdId(id);
          setSelectedLimitId(limitId);
          setSelectedLimitLabel(deriveLimitLabel(first));
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e.message ||
              "Không tải được danh sách Product Line / Failed to load product lines"
          );
        }
      } finally {
        if (!cancelled) {
          setLoadingProds(false);
        }
      }
    };

    loadProducts();
    return () => {
      cancelled = true;
    };
  }, [selectedProdId]);

  /** ------------------------------
   * DERIVED LABELS
   * ------------------------------ */
  const productLabel = useMemo(() => {
    if (!selectedProdId) return "";
    const row = findPLById(prodItems, selectedProdId);
    return row ? formatPL(row) : "";
  }, [prodItems, selectedProdId]);

  const operationLabel = useMemo(() => {
    if (!selectedOpId) return "";
    const idNum = Number(selectedOpId);
    const op = opsItems.find((o) => Number(o.option_id) === idNum);
    return op ? formatOp(op) : "";
  }, [opsItems, selectedOpId]);

  /** ------------------------------
   * HANDLERS
   * ------------------------------ */

  // Helper to clear downstream results when inputs change
  const resetResults = () => {
    setOpsItems([]);
    setSelectedOpId("");
    setLightData(null);
    setLightError("");
    setOperationText(
      "Kết quả kiểm tra kích thước sẽ hiển thị tại đây.\nSize check result will appear here."
    );
    setError("");
  };

  const handleProdChange = (e) => {
    const val = e.target.value || "";
    const row = findPLById(prodItems, val);
    const limitIdRaw = row ? row.size_limit_by ?? row.limit_id : null;
    const limitId = Number(limitIdRaw) || null;

    setSelectedProdId(val);
    setSelectedLimitId(limitId);
    setSelectedLimitLabel(row ? deriveLimitLabel(row) : "");

    // clear downstream: ops + additional data
    resetResults();
  };

  const handleWidthChange = (e) => {
    setWidthMm(e.target.value);
    resetResults(); // Reset state on change
  };

  const handleHeightChange = (e) => {
    setHeightMm(e.target.value);
    resetResults(); // Reset state on change
  };

  /**
   * REFACTORED: Centralized logic to select an op and fetch its details.
   * This allows us to auto-select single results.
   */
  const selectOperation = async (opId) => {
    setSelectedOpId(opId);

    if (!opId || !selectedProdId) {
      setLightData(null);
      setLightError("");
      return;
    }

    setLightLoading(true);
    setLightError("");
    setLightData(null);

    try {
      const params = new URLSearchParams({
        action: "light",
        prod_id: String(selectedProdId),
        selectedopsid: String(opId),
      });
      const data = await fetchJSON(`/api/size-check?${params.toString()}`);
      setLightData(data.item || null);
    } catch (err) {
      setLightError(err.message || "Network error (additional data)");
      setLightData(null);
    } finally {
      setLightLoading(false);
    }
  };

  // Main "Check" button – reuse SizeCheck backend: action=ops
  const handleCheck = async () => {
    setError("");
    setOperationText("");
    setOpsItems([]);
    setSelectedOpId("");
    setLightData(null);
    setLightError("");
    setShowNoResultsModal(false);

    if (!selectedProdId) {
      setError(
        "Vui lòng chọn 1 Product Line trước / Please select a Product Line."
      );
      return;
    }

    if (!widthMm || !heightMm) {
      setError(
        "Vui lòng nhập đầy đủ chiều rộng và chiều cao / Please enter both width and height."
      );
      return;
    }

    setLoadingOps(true);

    try {
      const params = new URLSearchParams({
        action: "ops",
        prod_id: String(selectedProdId),
        width: String(widthMm || ""),
        height: String(heightMm || ""),
      });

      // Pass HW/SHW style code, same as SizeCheck expects
      if (selectedLimitLabel) {
        params.set("limit_by_code", String(selectedLimitLabel));
      }

      const data = await fetchJSON(`/api/size-check?${params.toString()}`);
      const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];

      setOpsItems(items);

      if (items.length === 0) {
        // Trigger popup if no results
        setShowNoResultsModal(true);
        setOperationText(
          "Không có lựa chọn vận hành phù hợp.\nNo applicable operations for this size."
        );
      } else {
        setOperationText(
          `Có ${items.length} lựa chọn vận hành phù hợp.\n${items.length} applicable operation option(s) found.`
        );

        // FIX: If only 1 result, auto-select it immediately.
        // This solves the issue where clicking a single result (already highlighted by browser)
        // fails to trigger onChange.
        if (items.length === 1) {
          const onlyOp = items[0];
          await selectOperation(String(onlyOp.option_id));
        }
      }

    } catch (e) {
      setError(e.message || "Lỗi kiểm tra kích thước / Size check failed.");
      setOpsItems([]);
      setOperationText(
        "Kết quả kiểm tra kích thước sẽ hiển thị tại đây.\nSize check result will appear here."
      );
    } finally {
      setLoadingOps(false);
    }
  };

  // Wrapper for ListBox onChange
  const handleOpSelect = (e) => {
    selectOperation(e.target.value || "");
  };

  // Export handler for SizeCheckModal – simpler but compatible
  const handleDownloadConfig = async (payload) => {
    // must have exactly one operation
    if (!selectedOpId) {
      setError(
        "Vui lòng chọn đúng 1 lựa chọn vận hành / Please select exactly 1 operation option."
      );
      return;
    }

    try {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const ts =
        now.getFullYear().toString() +
        pad(now.getMonth() + 1) +
        pad(now.getDate()) +
        "_" +
        pad(now.getHours()) +
        pad(now.getMinutes()) +
        pad(now.getSeconds());

      const fullProdLabel =
        productLabel || (selectedProdId ? `prod-${selectedProdId}` : "product");
      
      // Clean for filename
      const safeProd = fullProdLabel.split("—")[0].trim();
      
      const safeOp = operationLabel || `op-${selectedOpId}`;

      const filename = `config_${safeProd}_${safeOp}_${ts}.txt`;

      const items = Array.isArray(payload?.items) ? payload.items : [];
      const lines = items.map((it) => {
        const label =
          it?.label ||
          it?.name ||
          it?.title ||
          it?.config ||
          it?.opt_name ||
          it?.pl ||
          "—";
        const value =
          it?.value ??
          it?.selected ??
          it?.text ??
          it?.opt_value ??
          it?.val ??
          "";
        const valText = Array.isArray(value) ? value.join(", ") : String(value);
        return `- ${label}: ${valText}`;
      });

      const header =
        `Product: ${safeProd} (id: ${selectedProdId})\n` +
        `Operation: ${safeOp} (id: ${selectedOpId})\n` +
        `Width: ${widthMm} mm\nHeight: ${heightMm} mm\n` +
        (selectedLimitLabel
          ? `Limit type: ${selectedLimitLabel}\n`
          : selectedLimitId
          ? `Limit type id: ${selectedLimitId}\n`
          : "") +
        "\nConfiguration:\n";

      const body = `${header}${lines.join("\n") || "- (none)"}`;

      const blob = new Blob([body], {
        type: "text/plain;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Lỗi xuất tập tin cấu hình / Export failed");
    }
  };

  // Helper for accessory trigger
  const isAccessoryTrigger = () => {
    const pid = Number(selectedProdId);
    const opId = Number(selectedOpId);
    const accOps = [981, 973, 974, 1041];
    return pid === 933 && accOps.includes(opId);
  };

  const handleConfigClick = () => {
    if (isAccessoryTrigger()) {
      setShowAccessoryModal(true);
    } else {
      setShowConfigModal(true);
    }
  };

  /** ------------------------------
   * RENDER
   * ------------------------------ */
  return (
    <div className="app-page">
      <div className="qc-shell">
        {/* Page-scoped CSS – keeps changes local to QuickCheck */}
        <style>{`
          /* NEW: Marquee Animation */
          @keyframes marqueeX { 0% { transform: translateX(0); } 100% { transform: translateX(-100%); } }
          
          .qc-marquee {
            margin-top: 6px;
            border: 1px dashed #e5e7eb;
            border-radius: 6px;
            overflow: hidden;
            padding: 3px 6px;
            font-size: 12px;
            background: #fafafa;
          }

          .qc-shell {
            max-width: 820px;
            margin: 0 auto;
          }

          .qc-card {
            background: #fff;
            border: 1px solid #e5e5e5;
            border-radius: 12px;
            padding: 18px 20px 20px;
            box-shadow: 0 1px 2px rgb(15 23 42 / 0.04);
          }

          .qc-title {
            font-size: 1.2rem;
            font-weight: 600;
            margin-bottom: 14px;
          }

          .qc-grid {
            display: grid;
            grid-template-columns: 1.4fr 1fr;
            gap: 18px;
          }

          .qc-label {
            font-weight: 600;
            margin-bottom: 6px;
          }

          .qc-field {
            margin-bottom: 12px;
          }

          .qc-right {
            align-self: flex-start;
          }

          .qc-ops-textarea {
            width: 100%;
            min-height: 160px;
            padding: 8px;
            border: 1px solid #e5e7eb;
            border-radius: 8px;
            font-family: inherit;
            font-size: 14px;
            background: #f9fafb;
          }

          .qc-note {
            font-size: 12px;
            opacity: 0.8;
            margin-top: 4px;
          }

          .qc-actions {
            margin-top: 16px;
            display: flex;
            gap: 8px;
          }

          .qc-error {
            margin-top: 12px;
            color: crimson;
            font-size: 13px;
          }

          @media (max-width: 840px) {
            .qc-grid {
              grid-template-columns: 1fr;
            }
            .qc-right {
              margin-top: 8px;
            }
          }
        `}</style>

        <div className="qc-card">
          <h2 className="qc-title">
            Kiểm tra kích thước sản phẩm / Quick Size Check
          </h2>

          {/* Top grid: Product line (left) + width/height (right) */}
          <div className="qc-grid">
            {/* Product line */}
            <div>
              <div className="qc-label">1) Sản phẩm</div>
              <div className="qc-field">
                <ListBox
                  multiple={false}
                  size={10}
                  value={selectedProdId ? [selectedProdId] : []}
                  onChange={handleProdChange}
                  disabled={loadingProds}
                >
                  {prodItems.length === 0 ? (
                    <option disabled>
                      {loadingProds
                        ? "Đang tải Product Line…"
                        : "Không có dữ liệu Product Line / No product lines."}
                    </option>
                  ) : (
                    prodItems.map((item) => (
                      <option
                        key={String(item.prod_id ?? item.opt_id)}
                        value={String(item.prod_id ?? item.opt_id)}
                      >
                        {formatPL(item)}
                      </option>
                    ))
                  )}
                </ListBox>
              </div>

              {/* NEW: Marquee Box */}
              {selectedProdId && (
                <div className="qc-marquee">
                  <div style={{ whiteSpace: "nowrap" }}>
                    <span
                      style={{
                        display: "inline-block",
                        paddingLeft: "100%",
                        animation: "marqueeX 12s linear infinite",
                      }}
                    >
                      {productLabel}
                    </span>
                  </div>
                </div>
              )}

              <div className="qc-note">
                Đã chọn / Selected:{" "}
                {selectedProdId
                  ? `${selectedProdId}${
                      selectedLimitLabel
                        ? ` (${selectedLimitLabel})`
                        : selectedLimitId
                        ? ` (${selectedLimitId})`
                        : ""
                    }`
                  : "(chưa chọn / none)"}
              </div>
            </div>

              {/* Width / Height + buttons (RIGHT column) */}
              <div className="qc-right">
              <div className="qc-field">
<TextInput
                  label="Chiều rộng / Width (mm)"
                  type="number"
                  value={widthMm}
                  onChange={handleWidthChange}
                  min={0}
                  helper={`≈ ${(Number(widthMm || 0) / 25.4).toFixed(
                    2
                  )} in | ½: ${Math.floor(Number(widthMm || 0) / 2)} mm | ⅓: ${Math.floor(Number(widthMm || 0) / 3)} mm`}
                />
              </div>

              <div className="qc-field">
<TextInput
                  label="Chiều cao / Height (mm)"
                  type="number"
                  value={heightMm}
                  onChange={handleHeightChange}
                  min={0}
                  helper={`≈ ${(Number(heightMm || 0) / 25.4).toFixed(
                    2
                  )} in | ½: ${Math.floor(Number(heightMm || 0) / 2)} mm | ⅓: ${Math.floor(Number(heightMm || 0) / 3)} mm`}
                />
              </div>

              <div className="qc-actions">
                <Button
                  onClick={handleCheck}
                  disabled={loadingProds || loadingOps}
                >
                  {loadingOps ? "Đang kiểm tra..." : "Kiểm tra / Check"}
                </Button>
                {/* NEW HDSD BUTTON */}
                <Button onClick={() => setGuideOpen(true)} style={{ marginLeft: 6 }}>
                  HDSD
                </Button>
              </div>
            </div>
          </div>

          {/* Operation section + Additional data */}
          <div style={{ marginTop: 16 }}>
             {/* CONFIG BUTTON MOVED TO HEADER */}
            <div className="qc-label" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
               <span>2) Vận hành</span>
               {selectedProdId && selectedOpId && (
                  <Button 
                    onClick={handleConfigClick}
                    style={{ padding: "4px 8px", fontSize: "13px" }}
                  >
                    Cấu hình SP để đặt hàng
                  </Button>
                )}
            </div>

            <div className="qc-field">
              {/* FIX: Removed array wrapper around selectedOpId for single select */}
              <ListBox
                multiple={false}
                size={8}
                value={selectedOpId}
                onChange={handleOpSelect}
                disabled={loadingOps || opsItems.length === 0}
              >
                {opsItems.length === 0 ? (
                  <option disabled>
                    {loadingOps
                      ? "Đang tải lựa chọn vận hành…"
                      : "Bấm Kiểm tra / Check để tải lựa chọn vận hành."}
                  </option>
                ) : (
                  opsItems.map((op) => (
                    <option
                      key={String(op.option_id)}
                      value={String(op.option_id)}
                    >
                      {formatOp(op)}
                    </option>
                  ))
                )}
              </ListBox>

              <div className="qc-note">
                Đã chọn / Selected:{" "}
                {selectedOpId || "(chưa chọn / none)"}
              </div>
            </div>

            {/* Summary + Additional data (copied from SizeCheck style) */}
            <div
              className="qc-ops-textarea"
              style={{ whiteSpace: "normal" }}
            >
              {/* Summary text */}
              <div style={{ fontSize: 13, marginBottom: 6 }}>
                {operationText || (
                  <>
                    Kết quả kiểm tra kích thước sẽ hiển thị tại đây.
                    <br />
                    Size check result will appear here.
                  </>
                )}
              </div>

              {/* Additional data */}
              <div
                style={{
                  borderTop: "1px solid #e5e7eb",
                  paddingTop: 6,
                  marginTop: 6,
                  fontSize: 12,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    opacity: 0.8,
                    marginBottom: 4,
                  }}
                >
                  Thông tin bổ sung / Additional data
                </div>

                {lightLoading ? (
                  <div>Đang tải…</div>
                ) : lightError ? (
                  <div style={{ color: "crimson" }}>Lỗi: {lightError}</div>
                ) : lightData ? (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 6,
                    }}
                  >
                    <div>
                      Hở sáng bên điều khiển / Light gap (ctrl):
                      <b> {lightData.ctrl_side ?? "-"}</b>
                    </div>
                    <div>
                      Hở sáng bên còn lại / Light gap (other):
                      <b> {lightData.bal_side ?? "-"}</b>
                    </div>
                    <div>
                      Độ sâu ray đỉnh / Headrail depth:
                      <b> {lightData.hr_depth ?? "-"}</b>
                    </div>
                    <div>
                      Chiều cao ray đỉnh / Headrail height:
                      <b> {lightData.hr_height ?? "-"}</b>
                    </div>
                    <div style={{ gridColumn: "1 / -1" }}>
                      Ghi chú / Note: <i>{lightData.note || "—"}</i>
                    </div>

                    {(lightData.min_width !== undefined ||
                      lightData.min_height !== undefined ||
                      lightData.maxof_max_width !== undefined ||
                      lightData.maxof_max_height !== undefined ||
                      lightData.max_m2 !== undefined) && (
                      <div
                        style={{
                          gridColumn: "1 / -1",
                          borderTop: "1px solid #ddd",
                          marginTop: 6,
                          paddingTop: 4,
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 600,
                            marginBottom: 2,
                          }}
                        >
                          Giới hạn kích thước / Size limits
                        </div>
                        <div>
                          Rộng tối thiểu / Min width:{" "}
                          <b>{lightData.min_width ?? "—"}</b> mm
                          <br />
                          Cao tối thiểu / Min height:{" "}
                          <b>{lightData.min_height ?? "—"}</b> mm
                        </div>
                        <div style={{ marginTop: 4 }}>
                          Rộng tối đa / Max width:{" "}
                          <b>{lightData.maxof_max_width ?? "—"}</b> mm
                          <br />
                          Cao tối đa / Max height:{" "}
                          <b>{lightData.maxof_max_height ?? "—"}</b> mm
                        </div>
                        <div style={{ marginTop: 4 }}>
                          Diện tích tối đa / Max area:{" "}
                          <b>{lightData.max_m2 ?? "—"}</b> m²
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ opacity: 0.75 }}>
                    Chọn 1 lựa chọn vận hành để xem chi tiết. / Select
                    one operation option to see details.
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && <div className="qc-error">✖ {error}</div>}
        </div>
      </div>

      {/* Shared configuration modal, same as SizeCheck */}
      <SizeCheckModal
        open={showConfigModal}
        onClose={() => setShowConfigModal(false)}
        width_mm={widthMm}
        height_mm={heightMm}
        prod_id={selectedProdId}
        prod_label={productLabel}
        operation_id={selectedOpId}
        operation_label={operationLabel}
        selectedOpIds={selectedOpId ? [selectedOpId] : []}
        limit_by_code={selectedLimitLabel}
        onDownload={handleDownloadConfig}
      />

      {/* NEW: Accessory Modal */}
      <AccessoryModal
        open={showAccessoryModal}
        onClose={() => setShowAccessoryModal(false)}
        prod_id={selectedProdId}
        prod_label={productLabel}
        operation_id={selectedOpId}
        operation_label={operationLabel}
        selectedOpIds={selectedOpId ? [selectedOpId] : []}
      />

      {/* NEW: No Results Warning Modal */}
      <Modal
        open={showNoResultsModal}
        title="Thông báo / Notice"
        onClose={() => setShowNoResultsModal(false)}
      >
        <div style={{ padding: "8px 0" }}>
          <p style={{ margin: "0 0 12px 0" }}>
            Không có sản phẩm nào phù hợp với kích thước bạn đã nhập.
            <br />
            <b>Vui lòng chia nhỏ chiều rộng hoặc chiều cao và thử lại.</b>
          </p>
          <hr style={{ border: 0, borderTop: "1px solid #eee", margin: "12px 0" }} />
          <p style={{ margin: 0, color: "#555", fontSize: "0.95em" }}>
            There's no suitable product for the size you entered.
            <br />
            <b>Please divide the width or height into smaller sizes and try again.</b>
          </p>
        </div>
      </Modal>

      {/* --- GUIDE MODAL --- */}
      <Modal
        open={guideOpen}
        title={activeGuide.title}
        onClose={() => setGuideOpen(false)}
        maxWidth={600}
      >
         <div className="box" style={{ margin: 0, border: "none", padding: 0 }}>
            <div style={{ marginBottom: 16 }}>
              {activeGuide.intro}
            </div>
            <ul style={{ paddingLeft: 20, margin: 0 }}>
              {activeGuide.steps.map((line, i) => (
                <li key={i} style={{ marginBottom: 12, whiteSpace: "pre-wrap" }}>
                  {line}
                </li>
              ))}
            </ul>
         </div>
      </Modal>

    </div>
  );
}